/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getCard,
  getHealth,
  getMe,
  getMyCard,
  logout,
  resolveCards,
  searchCards,
} from "@/lib/api/generated";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── Business cards ──────────────────────────────────────────────────────

/** The signed-in employee's own card, straight from Entra ID. */
export function useMyCard() {
  return useQuery({
    queryKey: ["card", "me"],
    queryFn: async () => {
      const { data } = await getMyCard();
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: (count, error) => {
      // 404 (no directory account) and 503 (directory down) will not fix
      // themselves within a retry window; everything else gets one retry.
      const status = (error as { status?: number }).status;
      return count < 1 && status !== 404 && status !== 503;
    },
  });
}

/** A colleague's card by Entra object id. */
export function useCard(userId: string | undefined) {
  return useQuery({
    queryKey: ["card", userId],
    queryFn: async () => {
      const { data } = await getCard({ path: { user_id: userId ?? "" } });
      return data;
    },
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Colleague lookup; the query is only sent once it is two characters long. */
export function useCardSearch(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ["card", "search", trimmed],
    queryFn: async () => {
      const { data } = await searchCards({ query: { q: trimmed } });
      return data;
    },
    enabled: trimmed.length >= 2,
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/** The cards of a print export, one e-mail address per colleague. */
export function useResolveCards() {
  return useMutation({
    mutationFn: async (addresses: string[]) => {
      const { data } = await resolveCards({ body: { addresses } });
      return data;
    },
  });
}
